import { DeviceFilters } from "@services/devices/types";
import _ from "lodash";
import {
  asBoolean,
  asNumber,
  asString,
  objectToSearchParams,
  SLFilterToSearch,
} from "./common";
import { correctWidgetMonitoringSourceValue, getMappedValue } from "./const";
import { convertLocalTime } from "@common/utils/localTime.utils";
import { convertCountryName } from "@assets/countries";

export const WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY =
  "timeWarrantyExpiration";
export const LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY = "timeLeaseExpiration";

export const getTimesForDevicesWarrantyExpirationFilter = (
  filter: DevicesWarrantyExpirationTimeFilter,
  payload?: DateInterval
): [start: number, end: number] => {
  let [start, end] = [
    convertLocalTime(new Date()),
    convertLocalTime(new Date()),
  ];
  switch (filter as DevicesWarrantyExpirationTimeFilter) {
    case "Custom time period":
      let timePayload = payload as DateInterval;
      start = new Date(timePayload?.startDate);
      end = new Date(timePayload?.endDate);
      break;
    case "Today":
      start.setHours(0, 0, 0, 1);
      end.setHours(23, 59, 59, 999);
      break;
    case "In the next week":
      end.setDate(start.getDate() + 7);
      break;
    case "In the next month":
      end.setMonth(start.getMonth() + 1);
      break;
    case "In the next 3 months":
      end.setMonth(start.getMonth() + 3);
      break;
  }
  return [convertLocalTime(start).getTime(), convertLocalTime(end).getTime()];
};
export const getTimesForDevicesLeaseExpirationFilter = (
  filter: DevicesLeaseExpirationTimeFilter,
  payload?: DateInterval
): [start: number, end: number] => {
  let [start, end] = [
    convertLocalTime(new Date()),
    convertLocalTime(new Date()),
  ];
  switch (filter as DevicesLeaseExpirationTimeFilter) {
    case "Custom time period":
      let timePayload = payload as DateInterval;
      start = new Date(timePayload?.startDate);
      end = new Date(timePayload?.endDate);
      break;
    case "Today":
      start.setHours(0, 0, 0, 1);
      end.setHours(23, 59, 59, 999);
      break;
    case "In the next week":
      end.setDate(start.getDate() + 7);
      break;
    case "In the next month":
      end.setMonth(start.getMonth() + 1);
      break;
    case "In the next 3 months":
      end.setMonth(start.getMonth() + 3);
      break;
  }
  return [convertLocalTime(start).getTime(), convertLocalTime(end).getTime()];
};

export const deviceStatusMap: {
  [K in DevicesStatusFilter]:
    | { key: keyof DeviceFilters; value: boolean }
    | undefined;
} = {
  All: undefined,
  Up: { key: "status.reachable", value: true },
  Unreachable: { key: "status.reachable", value: false },
  "Monitoring Stalled": {
    key: "status.stale",
    value: true,
  },
  "Monitoring Paused": {
    key: "status.pauseMonitoringStatus.paused",
    value: true,
  },
  "API Error": { key: "status.apiError", value: true },
  "Login Error": { key: "status.loginError", value: true },
  "In a Call": { key: "status.inCall", value: true },
  Unknown: { key: "status.linkUnknown", value: true },
  "Monitoring Triggers Paused": {
    key: "status.monitoringTriggersStatus.paused",
    value: true,
  },
};

export const sourceValues = {
  DMCA: "AVI-SPL Monitoring Agent",
  direct: "CommProxy",
  thirdParty: "3rd Party Management",
  controller: "Room Controller",
};

export const deviceSourceMap: {
  [K in AddDevicesSourceFilter]: string[];
} = {
  "3rd Party Management": [sourceValues.thirdParty],
  DMCA: [sourceValues.DMCA],
  "Room Controller": [sourceValues.controller],
  "Symphony Direct": [sourceValues.direct],
  "Symphony Direct & 3rd Party Management": [
    sourceValues.direct,
    sourceValues.thirdParty,
  ],
  "Symphony Direct & DMCA": [sourceValues.direct, sourceValues.DMCA],
  "Symphony Direct & Room Controller": [
    sourceValues.direct,
    sourceValues.controller,
  ],
  "3rd Party Management & DMCA": [sourceValues.thirdParty, sourceValues.DMCA],
  "Room Controller & DMCA": [sourceValues.controller, sourceValues.DMCA]
};
export const deviceSourceFilterMap: {
  [K in DevicesSourceFilter]: string[];
} = {
  "3rd Party Management": [sourceValues.thirdParty],
  DMCA: [sourceValues.DMCA],
  "Room Controller": [sourceValues.controller],
  "Symphony Direct": [sourceValues.direct],
};

export const widgetFilterToSLFilter = (
  filters: (WidgetFilter | FilterDefinition)[] = []
): DeviceFilters => {
  let mapped: DeviceFilters = {};
  const mapValue = (
    key: string,
    filterValue: string | number | boolean | string[] | undefined
  ) => {
    let value: typeof filterValue;
    switch (typeof filterValue) {
      case "boolean":
        value = filterValue;
        break;
      case "string":
      case "number":
        value = Boolean(filterValue) ? filterValue : undefined;
        break;
      case "object":
        Array.isArray(filterValue) && filterValue.every((v) => Boolean(v))
          ? (value = filterValue)
          : (value = undefined);
      default:
        value = filterValue;
        break;
    }
    return getMappedValue(
      mapped,
      key as keyof DeviceFilters,
      value
    ) as DeviceFilters;
  };

  filters.forEach((f) => {
    switch (f.filterType as FilterTypes & AdditionalFilterTypes) {
      case "status":
        const statusValue = f.payload
          ? Array.isArray(f.payload)
            ? f.payload
            : [f.payload]
          : [f.filterValue];
        statusValue.forEach((value) => {
          const deviceStatus = deviceStatusMap[value as DevicesStatusFilter];
          if (deviceStatus !== undefined) {
            mapped = mapValue(deviceStatus.key, deviceStatus.value);
          }
        });
        break;
      case "service":
        mapped = mapValue(
          "monitoringLevel",
          Array.isArray(f.payload)
            ? f.payload
            : Boolean(f.filterValue)
            ? [f.filterValue]
            : undefined
        );
        break;
      case "source":
        const sourceFilter: string[] = [];
        (Array.isArray(f.payload) ? f.payload : [f.filterValue]).forEach(
          (value) => {
            const sourceValue =
              deviceSourceMap[correctWidgetMonitoringSourceValue(value)];
            if (sourceValue) sourceFilter.push(...sourceValue);
          }
        );
        mapped = mapValue("monitoringSources", sourceFilter);
        break;
      case "vip":
        const vipValue = f.filterValue
          ? f.filterValue === "true" ||
            (f.filterValue as DevicesVIPFilter) === "VIP Devices"
          : f.filterValue &&
            (f.filterValue === "false" ||
              (f.filterValue as DevicesVIPFilter) === "Non-VIP Devices")
          ? false
          : undefined;
        mapped = mapValue(f.filterType as keyof DeviceFilters, vipValue);
        break;
      case "active":
        const value = f.filterValue
          ? f.filterValue === "true" ||
            (f.filterValue as DevicesActiveFilter) === "Active Devices"
          : f.filterValue &&
            (f.filterValue === "false" ||
              (f.filterValue as DevicesActiveFilter) === "Inactive Devices")
          ? false
          : undefined;
        mapped = mapValue(f.filterType as keyof DeviceFilters, value);
        break;
      case "target":
        const { manufacturer, deviceType, category, model } =
          f.payload as TargetFilterPayload;
        mapped = {
          ...mapped,
          manufacturer,
          model,
          deviceType: deviceType as string[], // ! to be remove
          category: category ? [category] : undefined,
        };
        break;
      case "accountId":
      case "room.location.id":
        mapped = mapValue(
          f.filterType as keyof DeviceFilters,
          Boolean(f.filterValue) ? f.filterValue : undefined
        );
        break;
      case LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY:
        const [leaseStart, leaseEnd] = getTimesForDevicesLeaseExpirationFilter(
          f.filterValue as DevicesLeaseExpirationTimeFilter,
          f.payload as DateInterval
        );
        mapped = mapValue("details.leaseExpirationDate.after", leaseStart);
        mapped = mapValue("details.leaseExpirationDate.before", leaseEnd);
        break;
      case WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY:
        const [warrantyStart, warrantyEnd] =
          getTimesForDevicesWarrantyExpirationFilter(
            f.filterValue as DevicesWarrantyExpirationTimeFilter,
            f.payload as DateInterval
          );
        mapped = mapValue(
          "details.warrantyExpirationDate.after",
          warrantyStart
        );
        mapped = mapValue("details.warrantyExpirationDate.before", warrantyEnd);
        break;
      case "deviceType":
        const deviceTypes = f.payload as TargetFilterPayload["deviceType"];
        mapped = mapValue("deviceType", deviceTypes);
        break;
      case "tags":
        mapped = mapValue(
          "tags",
          Array.isArray(f.payload) ? f.payload : undefined
        );
        break;
      case "room.details.roomType":
        mapped = mapValue(
          "room.details.roomType",
          Array.isArray(f.payload) ? f.payload : undefined
        );
        break;
      default:
        mapped = mapValue(f.filterType as keyof DeviceFilters, f.filterValue);
        break;
    }
  });
  return mapped;
};

export const widgetFilterToSearchParams = (
  filters?: (WidgetFilter | FilterDefinition)[]
) => {
  const asSL: any = widgetFilterToSLFilter(filters);
  const leaseExpirationTimeFilterType = filters?.find(
    ({ filterType }) =>
      (filterType as FilterTypes) === LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY
  );
  if (leaseExpirationTimeFilterType !== undefined) {
    asSL[LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY] = encodeURI(
      leaseExpirationTimeFilterType.filterValue
    );
  }
  if (
    decodeURI(asSL[LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY]) !==
    "Custom time period"
  ) {
    delete asSL["details.leaseExpirationDate.after"];
    delete asSL["details.leaseExpirationDate.before"];
  }
  const warrantyExpirationTimeFilterType = filters?.find(
    ({ filterType }) =>
      (filterType as FilterTypes) === WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY
  );
  if (warrantyExpirationTimeFilterType !== undefined) {
    asSL[WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY] = encodeURI(
      warrantyExpirationTimeFilterType.filterValue
    );
  }
  if (
    decodeURI(asSL[WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY]) !==
    "Custom time period"
  ) {
    delete asSL["details.warrantyExpirationDate.after"];
    delete asSL["details.warrantyExpirationDate.before"];
  }
  return objectToSearchParams(asSL);
};

const SLFilterToWidgetFilter = (
  slFilters: DeviceFilters
): FilterDefinition[] => {
  const filters: FilterDefinition[] = [];
  let targetPayload: TargetFilterPayload = {
    manufacturer: slFilters.manufacturer,
    deviceType: slFilters.deviceType,
    category: slFilters.category?.[0],
    model: slFilters.model,
  };
  if (Object.entries(targetPayload).some((x) => x[1]))
    filters.push({
      filterType: "target",
      filterValue: "",
      payload: targetPayload,
    });

  const statusPayload: string[] = [];
  if (slFilters["status.reachable"] !== undefined)
    statusPayload.push(
      slFilters["status.reachable"] === true ? "Up" : "Unreachable"
    );
  if (slFilters["status.apiError"] !== undefined)
    statusPayload.push("API Error");
  if (slFilters["status.loginError"] !== undefined)
    statusPayload.push("Login Error");
  if (slFilters["status.inCall"] !== undefined) statusPayload.push("In a Call");
  if (slFilters["status.stale"] !== undefined)
    statusPayload.push("Monitoring Stalled");
  if (slFilters["status.pauseMonitoringStatus.paused"] !== undefined)
    statusPayload.push("Monitoring Paused");
  if (slFilters["status.linkUnknown"] !== undefined)
    statusPayload.push("Unknown");
  if (slFilters["status.monitoringTriggersStatus.paused"] !== undefined)
    statusPayload.push("Monitoring Triggers Paused");

  if (slFilters["inACall"] !== undefined)
    filters.push({
      filterType: "inACall",
      filterValue: slFilters.inACall ? "true" : "false",
    });
  if (slFilters["vip"] !== undefined)
    filters.push({
      filterType: "vip",
      filterValue: slFilters.vip ? "true" : "false",
    });
  if (slFilters["callCapable"] !== undefined)
    filters.push({
      filterType: "callCapable",
      filterValue: slFilters.callCapable ? "true" : "false",
    });
  if (
    slFilters["details.leaseExpirationDate.after"] &&
    slFilters["details.leaseExpirationDate.before"]
  ) {
    let customValue: DevicesLeaseExpirationTimeFilter = "Custom time period";
    filters.push({
      filterType: LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
      filterValue: customValue,
      payload: {
        startDate: slFilters["details.leaseExpirationDate.after"],
        endDate: slFilters["details.leaseExpirationDate.before"],
      },
    });
  }
  if (
    slFilters["details.warrantyExpirationDate.after"] &&
    slFilters["details.warrantyExpirationDate.before"]
  ) {
    let customValue: DevicesWarrantyExpirationTimeFilter = "Custom time period";
    filters.push({
      filterType: WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY,
      filterValue: customValue,
      payload: {
        startDate: slFilters["details.warrantyExpirationDate.after"],
        endDate: slFilters["details.warrantyExpirationDate.before"],
      },
    });
  }
  if (slFilters["monitoringLevel"] !== undefined) {
    filters.push({
      filterType: "service",
      filterValue: "",
      payload: slFilters["monitoringLevel"] as string[],
    });
  }
  if (slFilters["monitoringSources"] !== undefined) {
    const slValue = slFilters["monitoringSources"];
    let filterPayload: DevicesSourceFilter[] = [];
    Object.entries(deviceSourceFilterMap).forEach(([key, v]) => {
      if (!slValue) return;
      slValue.forEach((value) => {
        if (v[0] === value) {
          filterPayload.push(key.toString() as DevicesSourceFilter);
        }
      });
    });

    if (filterPayload !== undefined)
      filters.push({
        filterType: "source",
        filterValue: "",
        payload: filterPayload,
      });
  }
  if (slFilters.watchList !== undefined) {
    filters.push({
      filterType: "watchList",
      filterValue: slFilters.watchList.toString(),
    });
  }
  if (slFilters.active !== undefined) {
    filters.push({
      filterType: "active",
      filterValue: slFilters.active ? "true" : "false",
    });
  }
  if (slFilters.provisioned !== undefined) {
    filters.push({
      filterType: "provisioned",
      filterValue: slFilters.provisioned ? "true" : "false",
    });
  }
  if (slFilters["room.managed"] !== undefined) {
    filters.push({
      filterType: "room.managed",
      filterValue: slFilters["room.managed"] ? "true" : "false",
    });
  }
  if (slFilters["isAggregator"] !== undefined) {
    filters.push({
      filterType: "isAggregator",
      filterValue: slFilters["isAggregator"] ? "true" : "false",
    });
  }
  if (slFilters.ucPlatform !== undefined) {
    filters.push({
      filterType: "ucPlatform",
      filterValue: slFilters.ucPlatform,
    });
  }
  if (slFilters.operatingSystem !== undefined) {
    filters.push({
      filterType: "operatingSystem",
      filterValue: slFilters.operatingSystem,
    });
  }
  if (slFilters["details.assetTag"] !== undefined) {
    filters.push({
      filterType: "details.assetTag",
      filterValue: slFilters["details.assetTag"],
    });
  }
  if (slFilters["accountId"] !== undefined) {
    filters.push({
      filterType: "accountId",
      filterValue: Array.isArray(slFilters["accountId"])
        ? slFilters["accountId"][0]
        : slFilters["accountId"],
    });
  }
  if (slFilters["room.location.name"] !== undefined) {
    filters.push({
      filterType: "room.location.name",
      filterValue: slFilters["room.location.name"],
    });
  }
  if (slFilters["room.location.city"] !== undefined)
    filters.push({
      filterType: "room.location.city",
      filterValue: slFilters["room.location.city"],
    });
  if (slFilters["name"] !== undefined) {
    filters.push({
      filterType: "name",
      filterValue: slFilters["name"],
    });
  }
  if (slFilters["serialNumber"] !== undefined) {
    filters.push({
      filterType: "serialNumber",
      filterValue: slFilters["serialNumber"],
    });
  }
  if (slFilters["softwareVersion"] !== undefined) {
    filters.push({
      filterType: "softwareVersion",
      filterValue: slFilters["softwareVersion"],
    });
  }
  if (slFilters["macAddress"] !== undefined) {
    filters.push({
      filterType: "macAddress",
      filterValue: slFilters["macAddress"],
    });
  }
  if (slFilters["ipAddress"] !== undefined) {
    filters.push({
      filterType: "ipAddress",
      filterValue: slFilters["ipAddress"],
    });
  }
  if (slFilters["gatekeeperIp"] !== undefined) {
    filters.push({
      filterType: "gatekeeperIp",
      filterValue: slFilters["gatekeeperIp"],
    });
  }
  if (slFilters["e164"] !== undefined) {
    filters.push({
      filterType: "e164",
      filterValue: slFilters["e164"],
    });
  }
  if (slFilters["sipUri"] !== undefined) {
    filters.push({
      filterType: "sipUri",
      filterValue: slFilters["sipUri"],
    });
  }
  if (slFilters["room.details.contactEmail"] !== undefined) {
    filters.push({
      filterType: "room.details.contactEmail",
      filterValue: slFilters["room.details.contactEmail"],
    });
  }
  if (slFilters["room.exchangeId"] !== undefined) {
    filters.push({
      filterType: "room.exchangeId",
      filterValue: slFilters["room.exchangeId"],
    });
  }
  if (slFilters["room.name"] !== undefined) {
    filters.push({
      filterType: "room.name",
      filterValue: slFilters["room.name"],
    });
  }
  if (slFilters["room.details.roomType"] !== undefined) {
    filters.push({
      filterType: "room.details.roomType",
      filterValue: "",
      payload: slFilters["room.details.roomType"],
    });
  }
  if (slFilters["room.location.country"] !== undefined) {
    filters.push({
      filterType: "room.location.country",
      filterValue: slFilters["room.location.country"],
    });
  }
  if (slFilters.tags !== undefined) {
    filters.push({
      filterType: "tags",
      filterValue: "",
      payload: slFilters.tags,
    });
  }
  if (statusPayload.length > 0)
    filters.push({
      filterType: "status",
      filterValue: "",
      payload: statusPayload,
    });

  return filters;
};

const searchToSLFilter = (params: URLSearchParams): DeviceFilters => {
  const filters: DeviceFilters = {};
  const temp = {
    monitoringSources: params.getAll("monitoringSources"),
    deviceType: params.getAll("deviceType"),
    monitoringLevel: params.getAll("monitoringLevel"),
    "room.id": params.getAll("room.id"),
    category: params.getAll("category"),
    accountId: params.getAll("accountId"),
    "room.location.country": params.get("room.location.country"),
    tags: params.getAll("tags"),
    "room.details.roomType": params.getAll("room.details.roomType"),
  };

  asString(filters, params, [
    "name",
    "room.location.name",
    "room.location.id",
    "ipAddress",
    "sipUri",
    "serialNumber",
    "softwareVersion",
    "gatekeeperIp",
    "model",
    "manufacturer",
    "room.name",
    "room.details.roomType",
    "room.details.roomNumber",
    "room.location.city",
    "macAddress",
    "e164",
    "room.exchangeId",
    "room.details.contactEmail",
    "ucPlatform",
    "operatingSystem",
    "details.assetTag",
  ]);

  asBoolean(filters, params, [
    "status.reachable",
    "status.inCall",
    "status.stale",
    "status.apiError",
    "status.loginError",
    "status.pauseMonitoringStatus.paused",
    "status.linkUnknown",
    "status.monitoringTriggersStatus.paused",
    "active",
    "watchList",
    "room.managed",
    "isAggregator",
    "provisioned",
    "vip",
    "callCapable",
    "inACall",
  ]);

  asNumber(filters, params, [
    "details.warrantyExpirationDate.after",
    "details.warrantyExpirationDate.before",
    "details.leaseExpirationDate.after",
    "details.leaseExpirationDate.before",
  ]);

  if (temp.monitoringSources.length > 0)
    filters.monitoringSources = temp.monitoringSources;
  if (temp.monitoringLevel.length > 0)
    filters.monitoringLevel = temp.monitoringLevel as DevicesServiceFilter[];
  if (temp.deviceType.length > 0) filters.deviceType = temp.deviceType;
  if (temp.category.length > 0) filters.category = temp.category;
  if (temp["room.id"].length !== 0) filters["room.id"] = temp["room.id"];
  if (temp.accountId.length > 0) filters.accountId = temp.accountId;
  if (temp["room.location.country"])
    filters["room.location.country"] = convertCountryName(
      temp["room.location.country"]
    );
  if (temp["room.details.roomType"].length > 0) {
    filters["room.details.roomType"] = temp["room.details.roomType"];
  }

  if (temp.tags.length > 0) filters.tags = temp.tags;

  return filters;
};

const searchToWidgetFilter = (params: URLSearchParams): FilterDefinition[] => {
  let definition = SLFilterToWidgetFilter(searchToSLFilter(params));
  let leaseExpirationTimeFilterType = decodeURI(
    params.get(LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY) ?? ""
  );
  if (
    leaseExpirationTimeFilterType !== "" &&
    leaseExpirationTimeFilterType !== "Custom time period"
  ) {
    definition.push({
      filterType: LEASE_EXPIRATION_TIME_FILTER_SEARCH_KEY,
      filterValue: leaseExpirationTimeFilterType,
    });
  }
  let warrantyExpirationTimeFilterType = decodeURI(
    params.get(WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY) ?? ""
  );
  if (
    warrantyExpirationTimeFilterType !== "" &&
    warrantyExpirationTimeFilterType !== "Custom time period"
  ) {
    definition.push({
      filterType: WARRANTY_EXPIRATION_TIME_FILTER_SEARCH_KEY,
      filterValue: warrantyExpirationTimeFilterType,
    });
  }

  return definition;
};

const omitSLFilters = (filters: DeviceFilters) =>
  _.omit(filters, ["watchList"]);

export default {
  fromDefinition: {
    toSL: (filters: (WidgetFilter | FilterDefinition)[] = [], omit = true) => {
      return omit
        ? omitSLFilters(widgetFilterToSLFilter(filters))
        : widgetFilterToSLFilter(filters);
    },
    toSearchParams: widgetFilterToSearchParams,
  },
  fromSL: {
    toDefinition: SLFilterToWidgetFilter,
    toSearchParams: (d: DeviceFilters) => SLFilterToSearch(d),
  },
  fromSearchParams: {
    toSL: (params: URLSearchParams, isOmit: boolean = true) =>
      isOmit
        ? omitSLFilters(searchToSLFilter(params))
        : searchToSLFilter(params),
    toDefinition: searchToWidgetFilter,
  },
};
